import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Mail, Flag, Calendar, Check, AlertTriangle, Briefcase } from 'lucide-react';
import { useApp } from '../context/AppContext';

// Admin buckets — same breakdown used in the Night Sort admin fee (RCTI & Invoicing left out).
// Tagging a task feeds the Admin Activity log we can later show AP.
export const ADMIN_TYPES = [
  'Shift allocation', 'Confirming shifts', 'Adjusting shifts', 'Discipline',
  'Briefings', 'Recruitment & onboarding', 'Compliance & WHS',
  'Fleet & maintenance', 'AP liaison', 'Leave management', 'Other admin',
];

const genId = () => 'todo_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDue = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return `${days[d.getDay()]} ${+m[3]} ${mon[+m[2] - 1]}`;
};

async function callAS(action, payload) {
  const res = await fetch('/.netlify/functions/todos-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  return res.json();
}

// Merge a fresh server fetch with the current list WITHOUT clobbering local changes:
// keep the locally-newer version of any task (an optimistic tick whose save hasn't
// been re-read yet), keep brand-new local tasks the server hasn't returned, and
// drop tasks the user just deleted. This makes ticks/edits/deletes stick even if a
// background re-fetch lands mid-save.
function mergeTodos(fetched, current, removed) {
  const out = (fetched || []).filter(f => !removed[f.id]).map(f => {
    const c = current.find(t => t.id === f.id);
    return (c && c.updatedAt && (!f.updatedAt || c.updatedAt > f.updatedAt)) ? c : f;
  });
  current.forEach(c => {
    if (!removed[c.id] && !(fetched || []).some(f => f.id === c.id)) out.push(c); // brand-new local task
  });
  return out;
}

// Open tasks first (overdue → soonest due → high priority → newest), completed last.
function sortTodos(list) {
  const today = todayISO();
  const rank = t => {
    if (t.done) return 9e15;
    let r = 0;
    if (t.dueDate) r = new Date(t.dueDate).getTime();
    else r = 8e15;                                   // no due date sinks below dated open items
    if (t.priority === 'high') r -= 5e11;            // nudge high priority up
    if (t.dueDate && t.dueDate < today) r -= 1e13;   // overdue floats to top
    return r;
  };
  return [...list].sort((a, b) => rank(a) - rank(b));
}

export default function ToDoList({ login, title = 'My to-do', headerAction = null, footerNote }) {
  const { addNotification } = useApp();
  const me = (login || '').toUpperCase();

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [newDue, setNewDue] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [newAdmin, setNewAdmin] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const removedRef = useRef({});   // ids deleted locally — never let a stale re-fetch resurrect them

  const load = useCallback(() => {
    if (!me) { setLoading(false); return; }
    setLoading(true);
    callAS('getToDos', { login: me })
      .then(d => { if (d.success) setTodos(prev => sortTodos(mergeTodos(d.todos || [], prev, removedRef.current))); })
      .catch(() => addNotification('Could not load tasks', 'error'))
      .finally(() => setLoading(false));
  }, [me, addNotification]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const text = newText.trim();
    if (!text || !me) return;
    const t = {
      id: genId(), text, done: false, dueDate: newDue,
      priority: newPriority, adminType: newAdmin,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    setTodos(prev => sortTodos([t, ...prev]));
    setNewText(''); setNewDue(''); setNewPriority('normal'); setNewAdmin('');
    try {
      const d = await callAS('saveToDo', { login: me, ...t });
      if (!d || !d.success) throw new Error(d && d.error);
    } catch (e) {
      setTodos(prev => prev.filter(x => x.id !== t.id));   // roll back so the user knows it didn't save
      addNotification('Could not save task — not added. Check connection and retry.', 'error');
    }
  };

  const patch = async (id, changes) => {
    // Compute the updated task OUTSIDE the state updater. React 18 doesn't run the
    // setTodos updater synchronously, so reading a value assigned inside it on the
    // next line is unreliable — that bug was skipping the save (ticks didn't stick).
    const cur = todos.find(t => t.id === id);
    if (!cur) return;
    const updated = { ...cur, ...changes, updatedAt: new Date().toISOString() };
    const snapshot = todos;
    setTodos(prev => sortTodos(prev.map(t => (t.id === id ? { ...t, ...changes, updatedAt: updated.updatedAt } : t))));
    try {
      const d = await callAS('saveToDo', { login: me, ...updated });
      if (!d || !d.success) throw new Error(d && d.error);
    } catch (e) {
      setTodos(snapshot);   // revert — never silently lose a tick
      addNotification('Could not save change — reverted. Check connection and retry.', 'error');
    }
  };

  const remove = async (id) => {
    removedRef.current[id] = true;
    setTodos(prev => prev.filter(t => t.id !== id));
    try {
      const d = await callAS('deleteToDo', { login: me, id });
      if (!d || !d.success) throw new Error(d && d.error);
    } catch (e) {
      delete removedRef.current[id];
      addNotification('Could not delete task — retry.', 'error');
      load();
    }
  };

  const remind = async (t) => {
    try {
      const res = await fetch('/.netlify/functions/todo-remind', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: me, task: t }),
      });
      const d = await res.json();
      if (d.success) addNotification('Reminder emailed', 'success');
      else addNotification('Could not send reminder: ' + (d.error || ''), 'error');
    } catch { addNotification('Could not send reminder', 'error'); }
  };

  const commitEdit = (id) => {
    if (editingId !== id) return;  // already committed (e.g. Enter then blur fire together)
    const txt = editText.trim();
    if (txt) patch(id, { text: txt });
    setEditingId(null); setEditText('');
  };

  const today = todayISO();
  const open = todos.filter(t => !t.done);
  const done = todos.filter(t => t.done);
  const visible = showDone ? todos : open;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: '1px solid var(--hp-border)' }}>
        <span style={{ fontSize: '1rem', fontWeight: 700 }}>{title}</span>
        <span className="badge badge-gold">{open.length} open</span>
        {me && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', color: 'var(--hp-dim)' }}>{me}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>{headerAction}</div>
      </div>

      <div style={{ padding: '16px 18px' }}>
        {/* Add row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <input
            className="form-input" placeholder={me ? 'Add a task…' : 'Sign in to add tasks'} disabled={!me}
            value={newText} onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            style={{ flex: '1 1 200px', minWidth: 0 }}
          />
          <input
            className="form-input" type="date" value={newDue} disabled={!me}
            onChange={e => setNewDue(e.target.value)}
            title="Due date (optional)" style={{ width: 150 }}
          />
          <button
            className="btn btn-sm" type="button" disabled={!me}
            onClick={() => setNewPriority(p => (p === 'high' ? 'normal' : 'high'))}
            title="Toggle High priority"
            style={{ borderColor: newPriority === 'high' ? 'var(--hp-red)' : 'var(--hp-border)', color: newPriority === 'high' ? 'var(--hp-red)' : 'var(--hp-dim)' }}
          >
            <Flag size={13} /> {newPriority === 'high' ? 'High' : 'Normal'}
          </button>
          <select
            className="form-select" value={newAdmin} disabled={!me}
            onChange={e => setNewAdmin(e.target.value)}
            title="Log as admin work (optional)" style={{ width: 170 }}
          >
            <option value="">Admin type… (none)</option>
            {ADMIN_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" type="button" onClick={add} disabled={!me || !newText.trim()}>
            <Plus size={14} /> Add
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', padding: '8px 0' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', padding: '12px 0', textAlign: 'center' }}>
            {open.length === 0 && done.length > 0 ? 'All done. Nice work.' : 'Nothing on the list yet.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visible.map(t => {
              const overdue = !t.done && t.dueDate && t.dueDate < today;
              const dueToday = !t.done && t.dueDate === today;
              return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)',
                  opacity: t.done ? 0.55 : 1,
                  borderLeft: overdue ? '3px solid var(--hp-red)' : dueToday ? '3px solid var(--hp-amber)' : t.priority === 'high' ? '3px solid var(--hp-red)' : '1px solid var(--hp-border)',
                }}>
                  {/* Checkbox */}
                  <button
                    type="button" onClick={() => patch(t.id, { done: !t.done })}
                    title={t.done ? 'Mark not done' : 'Mark done'}
                    style={{
                      width: 18, height: 18, flexShrink: 0, cursor: 'pointer', padding: 0,
                      borderRadius: 5, border: '1.5px solid ' + (t.done ? 'var(--hp-green)' : 'var(--hp-dim)'),
                      background: t.done ? 'var(--hp-green)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}
                  >{t.done && <Check size={12} />}</button>

                  {/* Text (click to edit) */}
                  {editingId === t.id ? (
                    <input
                      className="form-input" autoFocus value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onBlur={() => commitEdit(t.id)}
                      onKeyDown={e => { if (e.key === 'Enter') commitEdit(t.id); if (e.key === 'Escape') { setEditingId(null); setEditText(''); } }}
                      style={{ flex: 1, fontSize: '.85rem', padding: '2px 8px' }}
                    />
                  ) : (
                    <span
                      onClick={() => { setEditingId(t.id); setEditText(t.text); }}
                      style={{ flex: 1, fontSize: '.85rem', cursor: 'text', textDecoration: t.done ? 'line-through' : 'none', color: t.done ? 'var(--hp-dim)' : 'var(--hp-text)' }}
                    >{t.text}</span>
                  )}

                  {/* Admin tag */}
                  {t.adminType && (
                    <span title={'Admin: ' + t.adminType} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.68rem', color: 'var(--hp-purple)', whiteSpace: 'nowrap' }}>
                      <Briefcase size={11} /> {t.adminType}
                    </span>
                  )}

                  {/* Priority */}
                  {t.priority === 'high' && !t.done && (
                    <span className="badge badge-red" style={{ fontSize: '.65rem' }}>High</span>
                  )}

                  {/* Due chip */}
                  {t.dueDate && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.72rem', whiteSpace: 'nowrap',
                      color: overdue ? 'var(--hp-red)' : dueToday ? 'var(--hp-amber)' : 'var(--hp-dim)' }}>
                      {overdue ? <AlertTriangle size={12} /> : <Calendar size={12} />}
                      {overdue ? 'Overdue' : dueToday ? 'Today' : fmtDue(t.dueDate)}
                    </span>
                  )}

                  {/* Email reminder */}
                  <button type="button" onClick={() => remind(t)} title="Email me a reminder"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 2, display: 'flex' }}>
                    <Mail size={14} />
                  </button>
                  {/* Delete */}
                  <button type="button" onClick={() => remove(t.id)} title="Delete task"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 2, display: 'flex' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: '.72rem', color: 'var(--hp-dim)' }}>
          {done.length > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowDone(s => !s)}>
              {showDone ? 'Hide' : 'Show'} {done.length} completed
            </button>
          )}
          <span style={{ marginLeft: 'auto', textAlign: 'right' }}>{footerNote || 'Synced across your Dashboard, Portal & devices'}</span>
        </div>
      </div>
    </div>
  );
}
